/**
 * NL-006 / NL-010 / NL-014 / NL-018 (2026-06-09) — first-login
 * landing redirect after the coach advances the client to a new
 * step. The previous "last-seen step" is persisted per (clientId) in
 * localStorage; on mount, if `current_step` exceeds it, we redirect
 * to the section freshly unlocked by that step:
 *
 *   - Step 0 → 1: /check-in    (Daily Check-in unlocked)
 *   - Step 1 → 2: /carpe-diem  (Carpe Diem unlocked)
 *   - Step 2 → 3: /check-in    (Confidence Log unlocked)
 *   - Step 3 → 4: /check-in    (AM bookend locked — gratitude behavior added)
 *   - Step ≥ 5:   no automatic redirect (Weekly Focus already lives on
 *                 /check-in; the banner pattern doesn't ship past
 *                 NL1.6 today).
 *
 * The redirect fires at most once per (clientId, advance) pair: after
 * firing we update lastSeenStep to current_step so a navigation away
 * + back to /dashboard doesn't loop.
 *
 * Implementation note: lives in AppLayout so it has access to
 * useNavigate(); skipped when the user is already on the destination
 * URL (no-op).
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth-context';
import { useClientStep } from '@/lib/use-client-step';

const TRANSITION_REDIRECTS: Record<number, string> = {
  1: '/check-in',
  2: '/carpe-diem',
  3: '/check-in',
  4: '/check-in',
};

function lastSeenKey(clientId: string): string {
  return `h3eromap.lastSeenStep.${clientId}`;
}

function readLastSeen(clientId: string): number | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(lastSeenKey(clientId));
  if (raw === null) return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function writeLastSeen(clientId: string, step: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(lastSeenKey(clientId), String(step));
}

export function usePostStepTransitionRedirect(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { step, isClient, loading } = useClientStep();
  const clientId = user?.clientId ?? null;

  useEffect(() => {
    if (loading || !isClient || !clientId || step === null) return;
    const lastSeen = readLastSeen(clientId);

    // First-ever observation: just record the current step, no redirect.
    if (lastSeen === null) {
      writeLastSeen(clientId, step);
      return;
    }

    if (step <= lastSeen) return;

    // Walk forward from lastSeen+1 to step, picking the highest
    // transition that has a defined redirect target. (Coach can
    // advance multiple steps in a single backend write; redirect to
    // the most-recent unlock so the user lands on the newest surface.)
    let target: string | null = null;
    for (let s = lastSeen + 1; s <= step; s += 1) {
      const next = TRANSITION_REDIRECTS[s];
      if (next) target = next;
    }
    writeLastSeen(clientId, step);
    if (target && location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [clientId, isClient, loading, step, navigate, location.pathname]);
}

/** Test helper — drop the recorded last-seen step for a client. */
export function __resetLastSeenForTests(clientId: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(lastSeenKey(clientId));
}
